/**
 * Client progress dashboard. Real charts (Recharts) fed by GET /api/me/progress:
 *   - Workout:   per-exercise best-set weight trend + peak/volume/sessions
 *   - Body:      measurement metric trend (weight / body fat / chest / waist)
 *   - Nutrition: daily-calorie bars, real adherence %, real avg macros, water trend
 *   - Activity:  synced health bars (steps / active calories) + resting HR / VO2
 *   - PRs:       per-exercise PR timeline
 * Exercise lists come from what the user has actually logged, not a fixed list.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft, Dumbbell, PersonStanding, UtensilsCrossed, Trophy,
  ArrowUp, ArrowDown, TrendingUp, Activity, Droplets,
} from 'lucide-react';
import { api } from '@/lib/api';
import { TrendChart, BarSeries, MacroComboChart } from '@/components/charts/TrendChart';

type Tab = 'workout' | 'body' | 'nutrition' | 'activity' | 'prs';
type TimeRange = '1w' | '1m' | '3m' | '6m' | '1y';

const tabs: { id: Tab; label: string; icon: typeof Dumbbell }[] = [
  { id: 'workout', label: 'Lifts', icon: Dumbbell },
  { id: 'body', label: 'Body', icon: PersonStanding },
  { id: 'nutrition', label: 'Food', icon: UtensilsCrossed },
  { id: 'activity', label: 'Steps', icon: Activity },
  { id: 'prs', label: 'PRs', icon: Trophy },
];

const BODY_METRICS = [
  { label: 'Weight', key: 'weight' },
  { label: 'Body Fat', key: 'body_fat_pct' },
  { label: 'Lean Mass', key: 'lean_body_mass' },
  { label: 'Chest', key: 'chest' },
  { label: 'Waist', key: 'waist' },
] as const;

interface HistPoint { date: string; best_weight: number; volume: number; }
interface Measurement {
  date: string; weight: number | null; body_fat_pct: number | null; lean_body_mass: number | null; chest: number | null; waist: number | null;
}
interface PR {
  id: string; weight: number; reps: number; date: string;
  previous_best_weight: number | null; exercises: { name: string };
}
interface NutDay { date: string; calories: number; protein: number; carbs: number; fat: number; water: number; }
interface Targets { calories: number; protein: number; carbs: number; fat: number; waterOz: number; }
interface ProgressResponse {
  records: PR[];
  measurements: Measurement[];
  workoutCount: number;
  exerciseHistory: Record<string, HistPoint[]>;
  exercises: string[];
  nutritionHistory: NutDay[];
  targets: Targets;
}

interface HealthDay {
  date: string;
  steps: number | null;
  active_calories: number | null;
  exercise_minutes: number | null;
  sleep_hours: number | null;
  heart_rate_resting: number | null;
  weight: number | null;
  vo2_max: number | null;
}

export default function ProgressDetail() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('workout');
  const [timeRange, setTimeRange] = useState<TimeRange>('3m');
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [bodyMetric, setBodyMetric] = useState<(typeof BODY_METRICS)[number]>(BODY_METRICS[0]);
  const [prog, setProg] = useState<ProgressResponse | null>(null);
  const [health, setHealth] = useState<HealthDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get<ProgressResponse>(`/api/me/progress?range=${timeRange}`).catch(() => null),
      api.get<{ history?: HealthDay[] }>(`/api/health/history?range=${timeRange}`).catch(() => ({ history: [] })),
    ]).then(([p, h]) => {
      if (cancelled) return;
      setProg(p);
      setHealth(h?.history || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [timeRange]);

  const exList = prog?.exercises ?? [];
  const sel = selectedExercise && exList.includes(selectedExercise) ? selectedExercise : exList[0];
  const series = sel ? (prog?.exerciseHistory[sel] ?? []) : [];

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }}>
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1 className="display" style={{ fontSize: '1.9rem' }}>Progress</h1>
          <p className="text-sm" style={{ color: 'var(--gray-400)' }}>Track your gains</p>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex gap-1.5 mb-4 p-1 rounded-lg" style={{ background: 'var(--surface)' }}>
        {(['1w', '1m', '3m', '6m', '1y'] as TimeRange[]).map((r) => (
          <button
            key={r}
            onClick={() => setTimeRange(r)}
            className="flex-1 py-1.5 text-xs font-medium rounded-md transition-all"
            style={{
              background: timeRange === r ? 'var(--gold)' : 'transparent',
              color: timeRange === r ? 'var(--navy)' : 'var(--gray-400)',
            }}
          >
            {r.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2 mb-5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all"
              style={{
                background: active ? 'rgba(201,168,76,0.15)' : 'var(--surface)',
                color: active ? 'var(--gold)' : 'var(--gray-400)',
                border: active ? '1px solid var(--gold-muted)' : '1px solid transparent',
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex flex-col gap-4">
          {[1, 2].map((i) => <div key={i} className="card h-48 animate-pulse" style={{ background: 'var(--surface)' }} />)}
        </div>
      ) : activeTab === 'workout' ? (
        <WorkoutTab
          exList={exList} sel={sel} series={series} workoutCount={prog?.workoutCount ?? 0}
          onSelect={setSelectedExercise}
        />
      ) : activeTab === 'body' ? (
        <BodyTab measurements={prog?.measurements ?? []} health={health} metric={bodyMetric} onMetric={setBodyMetric} />
      ) : activeTab === 'nutrition' ? (
        <NutritionTab history={prog?.nutritionHistory ?? []} targets={prog?.targets} />
      ) : activeTab === 'activity' ? (
        <ActivityTab health={health} />
      ) : (
        <PrsTab exList={exList} sel={sel} records={prog?.records ?? []} onSelect={setSelectedExercise} />
      )}
    </div>
  );
}

/* ----------------------------- Workout ----------------------------- */
function WorkoutTab({ exList, sel, series, workoutCount, onSelect }: {
  exList: string[]; sel: string | undefined; series: HistPoint[]; workoutCount: number;
  onSelect: (s: string) => void;
}) {
  if (exList.length === 0) {
    return <div className="card text-center py-10"><p className="text-sm" style={{ color: 'var(--gray-400)' }}>No logged exercises yet. Complete a workout to see your trends.</p></div>;
  }
  const peak = series.length ? Math.max(...series.map((s) => s.best_weight)) : null;
  const vol = series.length ? Math.round(series.reduce((a, s) => a + s.volume, 0)) : null;
  return (
    <div className="flex flex-col gap-4">
      <ExerciseChips list={exList} sel={sel} onSelect={onSelect} />
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>Best Set Weight · {sel}</p>
        <TrendChart points={series.map((p) => ({ date: p.date, value: p.best_weight }))} unit="lbs" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatBlock icon={<ArrowUp size={14} />} label="Peak Weight" value={peak != null ? `${peak}` : '--'} color="var(--gold)" />
        <StatBlock icon={<TrendingUp size={14} />} label="Volume" value={vol != null ? vol.toLocaleString() : '--'} color="var(--info)" />
        <StatBlock icon={<Dumbbell size={14} />} label="Sessions" value={String(workoutCount)} color="var(--success)" />
      </div>
    </div>
  );
}

/* ------------------------------ Body ------------------------------- */
function BodyTab({ measurements, health, metric, onMetric }: {
  measurements: Measurement[]; health: HealthDay[]; metric: (typeof BODY_METRICS)[number];
  onMetric: (m: (typeof BODY_METRICS)[number]) => void;
}) {
  // Weight can come from manual measurements AND from synced health snapshots
  // (Apple Health / Health Connect). Merge both by date for the Weight metric so
  // a user who only syncs weight from their scale still sees the trend. Manual
  // entries win on a day that has both. Other body metrics are manual-only.
  // Lean mass uses the scale's lean_body_mass when present, else derives it from
  // weight × (1 − bodyfat%/100).
  const leanOf = (weight: number | null, bf: number | null, lean: number | null): number | null => {
    if (lean != null) return lean;
    if (weight != null && bf != null) return weight * (1 - bf / 100);
    return null;
  };
  const byDate = new Map<string, number>();
  if (metric.key === 'weight') {
    for (const h of health) if (h.weight != null) byDate.set(h.date, h.weight);
  }
  for (const m of measurements) {
    const v = metric.key === 'lean_body_mass'
      ? leanOf(m.weight, m.body_fat_pct, m.lean_body_mass)
      : m[metric.key];
    if (v != null) byDate.set(m.date, v as number);
  }
  const points = [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, value]) => ({ date, value }));
  const startVal = points[0]?.value ?? null;
  const currentVal = points[points.length - 1]?.value ?? null;
  const prevVal = points.length >= 2 ? points[points.length - 2].value : null;
  // Overall change = current vs first reading in range. Last change = current vs
  // the previous entry (day-over-day). Both need >=2 readings.
  const changeOverall = points.length >= 2 && startVal != null && currentVal != null ? currentVal - startVal : null;
  const changeLast = prevVal != null && currentVal != null ? currentVal - prevVal : null;
  // For chest & lean mass an increase is good; for weight/body-fat/waist a decrease is.
  const upIsGood = metric.key === 'chest' || metric.key === 'lean_body_mass';
  const goodOf = (c: number | null) => (upIsGood ? (c ?? 0) > 0 : (c ?? 0) < 0);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5 p-1 rounded-lg" style={{ background: 'var(--surface)' }}>
        {BODY_METRICS.map((m) => (
          <button key={m.key} onClick={() => onMetric(m)}
            className="flex-1 py-1.5 text-xs font-medium rounded-md transition-all"
            style={{ background: metric.key === m.key ? 'var(--gold)' : 'transparent', color: metric.key === m.key ? 'var(--navy)' : 'var(--gray-400)' }}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>{metric.label} Trend</p>
        <TrendChart points={points} color="#60a5fa" />
      </div>
      {points.length > 0 && (
        <div className="card">
          <div className="grid grid-cols-4 gap-0 text-center">
            <div><p className="text-[11px]" style={{ color: 'var(--gray-500)' }}>Start</p><p className="text-base font-bold text-white mt-1">{startVal != null ? Math.round(startVal * 10) / 10 : '--'}</p></div>
            <div className="border-l" style={{ borderColor: 'rgba(255,255,255,0.08)' }}><p className="text-[11px]" style={{ color: 'var(--gray-500)' }}>Current</p><p className="text-base font-bold text-white mt-1">{currentVal != null ? Math.round(currentVal * 10) / 10 : '--'}</p></div>
            <div className="border-l" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[11px]" style={{ color: 'var(--gray-500)' }}>vs Last</p>
              <p className="text-base font-bold mt-1 flex items-center justify-center gap-0.5" style={{ color: changeLast != null ? (goodOf(changeLast) ? 'var(--success)' : 'var(--error)') : 'var(--gray-400)' }}>
                {changeLast != null ? (<>{changeLast < 0 ? <ArrowDown size={13} /> : <ArrowUp size={13} />}{changeLast > 0 ? '+' : ''}{changeLast.toFixed(1)}</>) : '--'}
              </p>
            </div>
            <div className="border-l" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[11px]" style={{ color: 'var(--gray-500)' }}>Overall</p>
              <p className="text-base font-bold mt-1 flex items-center justify-center gap-0.5" style={{ color: changeOverall != null ? (goodOf(changeOverall) ? 'var(--success)' : 'var(--error)') : 'var(--gray-400)' }}>
                {changeOverall != null ? (<>{changeOverall < 0 ? <ArrowDown size={13} /> : <ArrowUp size={13} />}{changeOverall > 0 ? '+' : ''}{changeOverall.toFixed(1)}</>) : '--'}
              </p>
            </div>
          </div>
          <p className="text-[10px] text-center mt-2" style={{ color: 'var(--gray-600, var(--gray-500))' }}>Overall = change from your first reading in this range.</p>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Nutrition ---------------------------- */
function NutritionTab({ history, targets }: { history: NutDay[]; targets?: Targets }) {
  const t = targets ?? { calories: 2000, protein: 150, carbs: 250, fat: 65, waterOz: 64 };
  const waterPoints = history.filter((d) => d.water > 0).map((d) => ({ date: d.date, value: Math.round(d.water) }));

  // Real adherence: days with logged calories within ±15% of target.
  const loggedDays = history.filter((d) => d.calories > 0);
  const withinTarget = loggedDays.filter((d) => Math.abs(d.calories - t.calories) <= t.calories * 0.15).length;
  const adherence = loggedDays.length ? Math.round((withinTarget / loggedDays.length) * 100) : 0;

  // Real average macros across days with any logged food.
  const avg = (pick: (d: NutDay) => number) =>
    loggedDays.length ? Math.round(loggedDays.reduce((a, d) => a + pick(d), 0) / loggedDays.length) : 0;

  if (history.length === 0) {
    return <div className="card text-center py-10"><p className="text-sm" style={{ color: 'var(--gray-400)' }}>No nutrition logged in this range yet.</p></div>;
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>Macros &amp; Calories</p>
        <MacroComboChart days={history} />
      </div>

      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>Calorie Adherence</p>
        <div className="flex items-center justify-between mb-2">
          <p className="text-2xl" style={{ fontFamily: 'var(--font-display), Georgia, serif', lineHeight: 1.05, color: 'var(--cream)' }}>{adherence}%</p>
          <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{withinTarget} of {loggedDays.length} days within target</p>
        </div>
        <div className="w-full h-2 rounded-full" style={{ background: 'var(--navy)' }}>
          <div className="h-full rounded-full" style={{ width: `${adherence}%`, background: 'var(--success)' }} />
        </div>
      </div>

      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gold-muted)' }}>Average Daily Macros</p>
        <MacroRow label="Protein" avg={avg((d) => d.protein)} target={t.protein} color="var(--info)" />
        <MacroRow label="Carbs" avg={avg((d) => d.carbs)} target={t.carbs} color="var(--gold)" />
        <MacroRow label="Fat" avg={avg((d) => d.fat)} target={t.fat} color="var(--warning)" />
      </div>

      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: 'var(--water)' }}>
          <Droplets size={12} /> Water Intake
        </p>
        {waterPoints.length ? (
          <TrendChart points={waterPoints} color="var(--water, #38bdf8)" unit="oz" />
        ) : (
          <p className="text-sm py-6 text-center" style={{ color: 'var(--gray-500)' }}>No water logged in this range yet.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------- Activity ----------------------------- */
function ActivityTab({ health }: { health: HealthDay[] }) {
  if (health.length === 0) {
    return (
      <div className="card text-center py-8">
        <Activity size={32} style={{ color: 'var(--gold-muted)' }} className="mx-auto mb-2" />
        <p className="text-sm" style={{ color: 'var(--gray-400)' }}>No activity synced yet</p>
        <p className="text-xs mt-1" style={{ color: 'var(--gray-500)' }}>Connect Apple Health or Health Connect to see your trends.</p>
      </div>
    );
  }
  const steps = health.filter((h) => h.steps != null).map((h) => ({ date: h.date, value: Math.round(h.steps!) }));
  const cals = health.filter((h) => h.active_calories != null).map((h) => ({ date: h.date, value: Math.round(h.active_calories!) }));
  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>Steps</p>
        <BarSeries points={steps} color="var(--success, #4ade80)" />
      </div>
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--gold-muted)' }}>Active Calories</p>
        <BarSeries points={cals} color="var(--gold)" unit="kcal" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <StatTile label="Resting HR" value={lastVal(health, (h) => h.heart_rate_resting)} unit="bpm" color="var(--error)" />
        <StatTile label="VO₂ Max" value={lastVal(health, (h) => h.vo2_max)} unit="" color="var(--info)" />
      </div>
    </div>
  );
}

/* ------------------------------- PRs ------------------------------- */
function PrsTab({ exList, sel, records, onSelect }: {
  exList: string[]; sel: string | undefined; records: PR[]; onSelect: (s: string) => void;
}) {
  const prs = useMemo(() => records.filter((r) => !sel || r.exercises?.name === sel), [records, sel]);
  return (
    <div className="flex flex-col gap-4">
      {exList.length > 0 && <ExerciseChips list={exList} sel={sel} onSelect={onSelect} />}
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gold-muted)' }}>PR Timeline {sel ? `· ${sel}` : ''}</p>
        {prs.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--gray-500)' }}>No PRs recorded yet.</p>
        ) : prs.slice(0, 12).map((pr, i) => (
          <div key={pr.id || i} className="flex items-start gap-3 py-2" style={{ borderTop: i > 0 ? '1px solid rgba(255,255,255,0.06)' : undefined }}>
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--gold)' }} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">{pr.weight} lbs &times; {pr.reps} reps</p>
              <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{pr.date}</p>
            </div>
            {pr.previous_best_weight != null && pr.weight > pr.previous_best_weight && (
              <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-0.5" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
                <ArrowUp size={10} /> +{(pr.weight - pr.previous_best_weight).toFixed(0)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- shared ------------------------------ */
function ExerciseChips({ list, sel, onSelect }: { list: string[]; sel: string | undefined; onSelect: (s: string) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
      {list.map((ex) => (
        <button key={ex} onClick={() => onSelect(ex)}
          className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0"
          style={{ background: sel === ex ? 'var(--gold)' : 'var(--surface)', color: sel === ex ? 'var(--navy)' : 'var(--gray-400)' }}>
          {ex}
        </button>
      ))}
    </div>
  );
}

function StatBlock({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div className="card text-center">
      <div className="flex justify-center mb-1" style={{ color }}>{icon}</div>
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>{label}</p>
    </div>
  );
}

function MacroRow({ label, avg, target, color }: { label: string; avg: number; target: number; color: string }) {
  const pct = Math.min((avg / target) * 100, 100);
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-sm font-medium w-16" style={{ color }}>{label}</span>
      <div className="flex-1">
        <div className="flex justify-between text-xs mb-1">
          <span style={{ color: 'var(--gray-300)' }}>{avg}g avg</span>
          <span style={{ color: 'var(--gray-500)' }}>{target}g target</span>
        </div>
        <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--navy)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function lastVal(days: HealthDay[], pick: (h: HealthDay) => number | null): string {
  for (let i = days.length - 1; i >= 0; i--) {
    const v = pick(days[i]);
    if (v != null) return String(Math.round(v * 10) / 10);
  }
  return '--';
}

function StatTile({ label, value, unit, color }: { label: string; value: string; unit: string; color: string }) {
  return (
    <div className="card text-center">
      <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{label}</p>
      <p className="text-2xl mt-1" style={{ fontFamily: 'var(--font-display), Georgia, serif', lineHeight: 1.05, color }}>{value}</p>
      {unit && <p className="text-[10px]" style={{ color: 'var(--gray-500)' }}>{unit}</p>}
    </div>
  );
}
